import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
      
      // Try to get current organization from session (auth/me) first, then fallback to developer/me
      let currentOrgId = null;
      
      try {
        // Try /auth/me first - this returns the current session org_id (updated after switch)
        const meResponse = await api.get('/auth/me');
        currentOrgId = meResponse.data.org_id;
      } catch (meError) {
        // If /auth/me fails, try developer endpoint as fallback
        try {
          const devResponse = await api.get('/developer/me');
          if (devResponse.data.organizations && devResponse.data.organizations.length > 0) {
            // Developer: use first organization as fallback
            currentOrgId = devResponse.data.organizations[0].id;
          }
        } catch (devError) {
          // Not authenticated - that's okay, will use default org
        }
      }
      
      // Set current organization from session, or default to first one
      if (response.data.length > 0) {
        let currentOrg = null;
        
        if (currentOrgId) {
          // Try to find the organization from session
          currentOrg = response.data.find(org => org.id === currentOrgId);
        }
        
        if (currentOrg) {
          setCurrentOrg(currentOrg);
        } else {
          // Fallback to default if session org not found in list
          const defaultOrg = response.data.find(org => org.id === "00000000-0000-0000-0000-000000000001") || response.data[0];
          setCurrentOrg(defaultOrg);
        }
      }
    } catch (error) {
      // Don't log 401 errors - they're expected when not authenticated
      if (error.response?.status !== 401) {
        console.error('Failed to load organizations:', error);
      }
      // Set empty organizations if not authenticated
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (orgId) => {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    try {
      const response = await api.post(`/organizations/${orgId}/switch`);
      // Reload the page to refresh all data with new organization context
      // Don't update state here as page will reload
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  };

  const refreshOrganizations = async () => {
    await loadOrganizations();
  };

  const value = {
    organizations,
    currentOrg,
    loading,
    switchOrganization,
    refreshOrganizations,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
